import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, Timestamp, runTransaction } from "firebase/firestore"; 
import { useParams, useNavigate } from 'react-router-dom';

const formatCurrency = (num) => {
  if (typeof num !== 'number') { num = parseFloat(num || 0); }
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

function numberToWords(num) {
    const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
    const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];
    const inWordsBDTInternal = (n) => {
        let str = '';
        if (n > 9999999) { str += inWordsBDTInternal(Math.floor(n / 10000000)); str += 'Crore '; n %= 10000000; }
        if (n > 99999) { str += inWordsBDTInternal(Math.floor(n / 100000)); str += 'Lakh '; n %= 100000; }
        if (n > 999) { str += inWordsBDTInternal(Math.floor(n / 1000)); str += 'Thousand '; n %= 1000; }
        if (n > 99) { str += inWordsBDTInternal(Math.floor(n / 100)); str += 'Hundred '; n %= 100; }
        if (n > 19) { str += b[Math.floor(n / 10)] + ' ' + a[n % 10]; } else { str += a[n]; }
        return str; 
    };
    const taka = Math.round(parseFloat(num || 0));
    if (taka === 0) return 'BDT ZERO TAKA ONLY';
    let takaWords = inWordsBDTInternal(taka).trim(); 
    let result = 'BDT ' + takaWords + ' Taka Only'; 
    return result.replace(/\s+/g, ' ').toUpperCase();
}

const EditPayment = () => {
    const { paymentId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const [companyName, setCompanyName] = useState('');
    const [billMonth, setBillMonth] = useState('');
    const [invoiceDocId, setInvoiceDocId] = useState(''); 
    
    const [paymentEntries, setPaymentEntries] = useState([]);
    const [currentAmount, setCurrentAmount] = useState('');
    const [currentMethod, setCurrentMethod] = useState('Cash');
    const [currentEntryRemarks, setCurrentEntryRemarks] = useState(''); 
    
    const [paymentDate, setPaymentDate] = useState('');
    // 👇 নতুন স্টেট
    const [amountDueDate, setAmountDueDate] = useState('');
    const [authorizedBy, setAuthorizedBy] = useState('MD. Morshed');
    

    useEffect(() => {
        const fetchPayment = async () => {
            if (!paymentId) return;
            setLoading(true);
            try {
                const docRef = doc(db, "payments", paymentId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setCompanyName(data.companyName);
                    setBillMonth(data.billMonth);
                    setInvoiceDocId(data.invoiceDocId); 
                    setPaymentDate(new Date(data.paymentTimestamp.toDate()).toISOString().split('T')[0]);
                    setAuthorizedBy(data.authorizedBy || 'MD. Morshed');
                    
                    // 👇 Load optional date logic
                    if (data.amountDueDate) {
                         const [d, m, y] = data.amountDueDate.split('/');
                         setAmountDueDate(`${y}-${m}-${d}`);
                    }

                    if (data.paymentEntries) {
                        setPaymentEntries(data.paymentEntries.map((entry, index) => ({
                            ...entry, 
                            id: index, 
                            remarks: entry.remarks || entry.chequeNo || '' 
                        }))); 
                    } else {
                        setPaymentEntries([{
                            id: 1, 
                            amount: data.amount, 
                            method: data.method, 
                            remarks: data.chequeNo || data.remarks || ''
                        }]);
                    }
                } else {
                    setMessage("Error: Receipt not found.");
                }
            } catch (error) {
                console.error("Error fetching payment: ", error);
                setMessage("Error loading payment data.");
            }
            setLoading(false);
        };
        fetchPayment();
    }, [paymentId]);

    const handleAddEntry = () => {
        if (!currentAmount || parseFloat(currentAmount) <= 0) {
            alert("Please enter a valid amount.");
            return;
        }
        setPaymentEntries(prev => [
            ...prev,
            {
                id: Date.now(), 
                amount: parseFloat(currentAmount),
                method: currentMethod,
                remarks: currentEntryRemarks 
            }
        ]);
        setCurrentAmount('');
        setCurrentMethod('Cash');
        setCurrentEntryRemarks('');
    };

    const handleRemoveEntry = (id) => {
        setPaymentEntries(prev => prev.filter(entry => entry.id !== id));
    };

    const totalAmount = paymentEntries.reduce((acc, entry) => acc + entry.amount, 0);

    const handleUpdatePayment = async (e) => {
        e.preventDefault();
        if (paymentEntries.length === 0) {
            alert("Cannot save receipt with no payment entries. Delete the full receipt instead.");
            return;
        }
        
        setLoading(true);
        setMessage('');

        // 👇 Format date for saving
        let formattedAmountDueDate = null;
        if (amountDueDate) {
            const [y, m, d] = amountDueDate.split('-');
            formattedAmountDueDate = `${d}/${m}/${y}`;
        }

        try {
            await runTransaction(db, async (transaction) => {
                const paymentRef = doc(db, "payments", paymentId);
                const invoiceRef = doc(db, "invoices", invoiceDocId);

                const invoiceSnap = await transaction.get(invoiceRef);
                if (!invoiceSnap.exists()) {
                    throw new Error("Invoice document not found!");
                }
                const invoiceData = invoiceSnap.data();
                
                const newTotalAmount = paymentEntries.reduce((acc, entry) => acc + entry.amount, 0);
                const updatedPaymentData = {
                    paymentEntries: paymentEntries.map(({id, ...rest}) => rest), 
                    totalAmount: newTotalAmount,
                    amountInWords: numberToWords(newTotalAmount),
                    date: new Date(paymentDate).toLocaleDateString('en-GB'),
                    paymentTimestamp: Timestamp.fromDate(new Date(paymentDate)),
                    // 👇 Save updated amount due date
                    amountDueDate: formattedAmountDueDate,
                    authorizedBy: authorizedBy
                };
                
                const updatedPaidEntries = (invoiceData.paidEntries || []).map(entry => {
                    if (entry.paymentDocId === paymentId) {
                        return { 
                            ...entry, 
                            amount: newTotalAmount, 
                            date: new Date(paymentDate).toLocaleDateString('en-GB'),
                            method: paymentEntries.length > 1 ? 'Multiple' : paymentEntries[0].method,
                        };
                    }
                    return entry; 
                });
                
                const items = invoiceData.items || [];
                const exchangeRate = invoiceData.exchangeRate || 100;
                const previousDue = invoiceData.previousDue || 0;
                const discount = invoiceData.discountAmount || 0;
                
                const calculateItemTotal = (item) => {
                    const rate = parseFloat(item.rate || 0); const fsc = parseFloat(item.fsc || 0);
                    const demand = parseFloat(item.demandCharge || 0); const weight = parseFloat(item.weight || 0);
                    return (rate + fsc + (demand * weight));
                };
                const totalBdt = items.reduce((acc, item) => acc + calculateItemTotal(item), 0) * exchangeRate;
                
                let newTotalPaid;
                if (invoiceData.allowEditTotalPaid) {
                    newTotalPaid = invoiceData.totalPaid; 
                } else {
                    newTotalPaid = updatedPaidEntries.reduce((acc, entry) => acc + parseFloat(entry.amount || 0), 0);
                }
                const finalTotalBdt = (totalBdt + previousDue) - newTotalPaid - discount;

                transaction.update(paymentRef, updatedPaymentData);
                transaction.update(invoiceRef, {
                    paidEntries: updatedPaidEntries,
                    totalPaid: newTotalPaid,
                    finalTotalBdt: finalTotalBdt
                });
            });

            setMessage('Payment and Invoice updated successfully!');
            setTimeout(() => navigate('/dashboard/payments'), 1500); 

        } catch (error) {
            console.error("Error updating payment: ", error);
            setMessage(`Error: ${error.message}`);
            setLoading(false); 
        }
    };

    const handleDeletePayment = async () => {
        if (!window.confirm("Are you sure you want to delete this payment receipt? This will also update the invoice total.")) {
            return;
        }
        setLoading(true);
        setMessage('');

        try {
            await runTransaction(db, async (transaction) => {
                const paymentRef = doc(db, "payments", paymentId);
                const invoiceRef = doc(db, "invoices", invoiceDocId);

                const invoiceSnap = await transaction.get(invoiceRef);
                if (!invoiceSnap.exists()) {
                    throw new Error("Invoice document not found!");
                }
                const invoiceData = invoiceSnap.data();

                const updatedPaidEntries = (invoiceData.paidEntries || []).filter(entry => 
                    entry.paymentDocId !== paymentId
                );
                
                const items = invoiceData.items || [];
                const exchangeRate = invoiceData.exchangeRate || 100;
                const previousDue = invoiceData.previousDue || 0;
                const discount = invoiceData.discountAmount || 0;
                
                const calculateItemTotal = (item) => {
                    const rate = parseFloat(item.rate || 0); const fsc = parseFloat(item.fsc || 0);
                    const demand = parseFloat(item.demandCharge || 0); const weight = parseFloat(item.weight || 0);
                    return (rate + fsc + (demand * weight));
                };
                const totalBdt = items.reduce((acc, item) => acc + calculateItemTotal(item), 0) * exchangeRate;
                
                let newTotalPaid;
                if (invoiceData.allowEditTotalPaid) {
                    newTotalPaid = invoiceData.totalPaid; 
                } else {
                    newTotalPaid = updatedPaidEntries.reduce((acc, entry) => acc + parseFloat(entry.amount || 0), 0);
                }
                const finalTotalBdt = (totalBdt + previousDue) - newTotalPaid - discount;

                transaction.delete(paymentRef); 
                transaction.update(invoiceRef, { 
                    paidEntries: updatedPaidEntries,
                    totalPaid: newTotalPaid,
                    finalTotalBdt: finalTotalBdt
                });
            });

            setMessage('Payment deleted and invoice updated successfully!');
            setTimeout(() => navigate('/dashboard/payments'), 2000); 

        } catch (error) {
            console.error("Error deleting payment: ", error);
            setMessage(`Error: ${error.message}`);
            setLoading(false); 
        }
    };

    return (
        <div className='payment-container'>
            <p className='new-invoice-heading'>Edit Payment Receipt</p>
            
            <form onSubmit={handleUpdatePayment}>
                <div className='form-section'>
                    <h3>Bill Information (Read-Only)</h3>
                    <div className='form-grid' style={{gridTemplateColumns: '1fr 1fr'}}>
                        <div><label>Company:</label><input value={companyName} disabled /></div>
                        <div><label>Bill Month:</label><input value={billMonth} disabled /></div>
                    </div>
                </div>
                
                <div className='form-section'>
                    <h3>Payment Entries (Editable)</h3>
                    
                    {paymentEntries.length > 0 && (
                        <table className='invoice-preview-table' style={{marginTop: '0px', fontSize: '14px'}}>
                            <thead>
                                <tr>
                                    <th>Method</th>
                                    <th>Remarks</th>
                                    <th>Amount</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paymentEntries.map(entry => (
                                    <tr key={entry.id}>
                                        <td>{entry.method}</td>
                                        <td>{entry.remarks || entry.chequeNo}</td> 
                                        <td style={{textAlign: 'right'}}>{formatCurrency(entry.amount)}</td>
                                        <td style={{textAlign: 'center'}}>
                                            <button type="button" onClick={() => handleRemoveEntry(entry.id)} className='remove-btn-small'>X</button>
                                        </td>
                                    </tr>
                                ))}
                                <tr>
                                    <td colSpan="2" style={{textAlign: 'right', fontWeight: 'bold'}}>NEW TOTAL:</td>
                                    <td style={{textAlign: 'right', fontWeight: 'bold'}}>{formatCurrency(totalAmount)}</td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                    
                    <div className='form-grid' style={{gridTemplateColumns: '1fr 1fr 1fr auto', gap: '15px', alignItems: 'flex-end', borderTop: '1px solid #eee', paddingTop: '20px', marginTop: '20px'}}>
                        <div>
                            <label>Payment Method:</label>
                            <select value={currentMethod} onChange={e => setCurrentMethod(e.target.value)}>
                                <option value="Cash">Cash</option>
                                <option value="Cheque">Cheque</option>
                                <option value="Bank Transfer">Bank Transfer</option>
                            </select>
                        </div>
                        <div>
                            <label>Amount (BDT):</label>
                            <input type='number' value={currentAmount} onChange={e => setCurrentAmount(e.target.value)} placeholder='0.00' style={{textAlign: 'right'}} />
                        </div>
                        <div>
                            <label>Remarks (for this entry):</label>
                            <input type='text' value={currentEntryRemarks} onChange={e => setCurrentEntryRemarks(e.target.value)} placeholder='e.g., Cheque no 123' />
                        </div>
                        <button type="button" className='add-btn' onClick={handleAddEntry} style={{margin: 0}}>Add Entry</button>
                    </div>
                </div>
                
                <div className='form-section'>
                    <h3>Receipt Details (Editable)</h3>
                    <div className='form-grid' style={{gridTemplateColumns: '1fr 1fr 1fr'}}>
                        <div>
                            <label>Payment Date:</label>
                            <input type='date' value={paymentDate} onChange={e => setPaymentDate(e.target.value)} required />
                        </div>
                        {/* 👇 এখানে নতুন ডেট ইনপুট */}
                        <div>
                            <label>Amount Due Date (Optional):</label>
                            <input 
                                type='date' 
                                value={amountDueDate} 
                                onChange={e => setAmountDueDate(e.target.value)} 
                                title="If selected, this date will appear in 'AMOUNT DUE @ [Date]'. If empty, no date or @ will be shown."
                            />
                        </div>
                        <div>
                            <label>Authorized By (Signature):</label>
                            <select value={authorizedBy} onChange={e => setAuthorizedBy(e.target.value)}>
                                <option value="MD. Morshed">MD. Morshed</option>
                                <option value="Anis Ahmed">Anis</option>
                            </select>
                        </div>
                    </div>
                </div>
                
                <div className='button-group'>
                    <button type='submit' className='save-invoice-btn' disabled={loading}>
                        {loading ? 'Updating...' : 'Update Payment Details'}
                    </button>
                    <button type="button" className='delete-btn' disabled={loading} onClick={handleDeletePayment}>
                        {loading ? 'Deleting...' : 'Delete Full Receipt'}
                    </button>
                </div>
                {message && <p style={{textAlign: 'center', marginTop: '10px'}}>{message}</p>}
            </form>
        </div>
    );
};

export default EditPayment;